import type { HTMLMotionProps } from 'motion/react'
import { cloneElement, isValidElement, type ReactNode } from 'react';
import { motion } from 'motion/react'
import clsx from 'clsx';
import type { IconProps } from '../icons/types';
import TextDefault from '../texts/TextDefault';
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint';

type ButtonIconProps = Omit<HTMLMotionProps<'button'>, 'color'> & {
    icon?: ReactNode;
    outlined?: boolean;
    size?: 'default' | 'large'
    color?: 'blue' | 'orange' | 'red' | 'white'
    children: string;
    className?: string;
    //  use for stacked label lines and `\n` breaks
    multiline?: boolean
    maxCharsPerLine?: number
}

const iconPropsByColor: Record<NonNullable<ButtonIconProps['color']>, IconProps> = {
    blue: {
        stroke: '#0A2897',
        stopColor: '#C8E3F7',
    },
    red: {
        stroke: '#7F0016',
        stopColor: '#F3A9B6',
    },
    orange: {
        stroke: '#8A0B00',
        stopColor: '#FFD080',
    },
    white: {
        stroke: '#5486AC',
        stopColor: '#C8E3F7',
    },
}

const disabledIconProps: IconProps = {
    stroke: '#7B7E91',
    stopColor: '#C8E3F7',
}

const textPropsByColor: Record<NonNullable<ButtonIconProps['color']>, {
    textColor: string
    outlineColor: string
    dropShadowColor: string
}> = {
    blue: {
        textColor: '#C8E3F7',
        outlineColor: '#0A2897',
        dropShadowColor: '#0A2897',
    },
    red: {
        textColor: '#F3A9B6',
        outlineColor: '#7F0016',
        dropShadowColor: '#7F0016',
    },
    orange: {
        textColor: '#FFD080',
        outlineColor: '#8A0B00',
        dropShadowColor: '#8A0B00',
    },
    white: {
        textColor: '#5486AC',
        outlineColor: '#5486AC',
        dropShadowColor: '#5486AC',
    },
}

export default function Button({
    icon,
    size = 'default',
    outlined,
    children,
    color = 'blue',
    disabled,
    className,
    multiline = false,
    maxCharsPerLine,
    ...buttonProps
}: ButtonIconProps) {
    const iconProps = {
        ...iconPropsByColor[color],
        ...(disabled ? disabledIconProps : {}),
    }
    const renderedIcon = isValidElement<IconProps>(icon)
        ? cloneElement(icon, iconProps)
        : icon
    const textProps = textPropsByColor[color]

    const isStele = useSteleBreakpoint();
    const animatedY = isStele ? -10 : -2.5
    const usesOutlinedSizing = Boolean(outlined)

    const textSizeMobile = usesOutlinedSizing
        ? 12
        : multiline
            ? 16
            : size === 'default'
                ? 12
                : 16

    return (
        <div
            className={clsx(
                'flex justify-center items-center rounded-full w-full',
                usesOutlinedSizing && 'bg-white-gradient h-10.5 max-h-10.5 stele:h-42 stele:max-h-42 px-0.75 stele:px-3 drop-shadow-2-white-stroke',
                className
            )}>
            <div className={clsx(
                'flex justify-center items-center rounded-full w-full',
                usesOutlinedSizing
                    ? 'h-9 max-h-9 stele:h-36 stele:max-h-36'
                    : multiline
                        ? 'min-h-11 h-auto py-0.5 stele:min-h-32 stele:py-2'
                        : size === 'default'
                            ? 'h-8 max-h-8'
                            : 'h-11 max-h-11',
                !usesOutlinedSizing && !multiline && 'stele:h-32 stele:max-h-32',
                color === 'blue' && 'bg-blue-stroke',
                color === 'red' && 'bg-red-stroke',
                color === 'orange' && 'bg-orange-stroke',
                color == 'white' && 'bg-white-stroke-darker',

                disabled && 'border-transparent! bg-transparent!'
            )}>
                <motion.button
                    {...buttonProps}
                    type={'button'}
                    disabled={disabled}
                    initial={false}
                    animate={disabled
                        ? { y: 0 }
                        : { y: animatedY }
                    }
                    whileTap={
                        disabled
                            ? undefined
                            : { y: 0 }
                    }
                    transition={{
                        y: {
                            type: 'spring',
                            stiffness: 520,
                            damping: 30,
                            mass: 0.7,
                        },
                    }}
                    data-ui-priority
                    className={clsx(
                        'relative flex justify-center items-center bg-blue-gradient rounded-full w-full',
                        multiline ? 'min-h-full h-full px-2 py-1' : 'h-full',
                        'gap-2.5 border-2',
                        'stele:border-8',
                        color === 'blue' && 'bg-blue-gradient border-blue-stroke',
                        color === 'red' && 'border-red-stroke bg-red-gradient',
                        color === 'orange' && 'border-orange-stroke bg-orange-gradient',
                        color === 'white' && 'border-white-stroke-darker bg-white-gradient',
                        disabled && 'border-white-stroke! bg-grey-gradient!',

                    )}
                >
                    <div
                        className={clsx(
                            'absolute bg-shine-gradient rounded-full',
                            usesOutlinedSizing
                                ? 'top-0.75 w-[85%] h-2 stele:top-2.5 stele:w-[85%] stele:h-9'
                                : 'top-0.75 w-[85%] h-2 stele:top-2 stele:w-[85%] stele:h-8'
                        )}
                    />
                    {color !== 'white' ?
                        <TextDefault
                            fontFamily='grandstander'
                            fontWeight={900}
                            italic
                            tracking='0.02em'
                            textColor={textProps.textColor}
                            outlineColor={textProps.outlineColor}
                            dropShadowColor={textProps.dropShadowColor}
                            fontSize={isStele ? 48 : textSizeMobile}
                            maxCharsPerLine={maxCharsPerLine ?? (multiline ? 24 : 32)}
                            leading={multiline ? 1.1 : 1.5}
                            className='uppercase leading-0 shrink-0 w-full'
                        >
                            {children}
                        </TextDefault>
                        :
                        <p className='text-white-stroke-darker font-grandstander font-black text-[12px] stele:text-[48px] italic uppercase leading-[85%]'>
                            {children}
                        </p>
                    }
                    {icon &&
                        <div className={clsx(
                            'flex justify-center items-center shrink-0',
                            usesOutlinedSizing
                                ? 'max-w-4.5 max-h-4.5 stele:max-w-18 stele:max-h-18'
                                : size === 'default'
                                    ? 'max-w-4 max-h-4'
                                    : 'max-w-6 max-h-6',
                            !usesOutlinedSizing && 'stele:max-w-16 stele:max-h-16'
                        )}
                        >
                            {renderedIcon}
                        </div>
                    }
                </motion.button >
            </div >
        </div>
    )
}

